import React, { useCallback, useMemo } from 'react';
import * as Sentry from '@sentry/react';
import { Button } from '@/components/ui/button';
import {
  AlertTriangle,
  FilesIcon,
  AlertCircle,
  EyeIcon,
  TextCursorInputIcon,
  AppWindowIcon,
  Redo2,
  AlertCircleIcon,
  GitBranchIcon,
  NetworkIcon,
} from 'lucide-react';
import { useWorkflowValidationStore } from '@/stores/workflow-validation-store';
import { useNodeStore } from '@/stores/node-store';
import { useWorkflowStore } from '@/stores/workflow-store';
import { useWorkflowPanelStore } from '@/stores/workflow-panel-store';
import {
  uploadFile,
  FileUploadResponse,
} from '@/services/workflow/upload-file';
import { useNotification } from '@/components/notifications';
import {
  InputValue,
  NodeInputType,
  NodeType,
  NodeStruct,
  HandleType,
} from '@/types/node-types';
import { CustomEdge } from '@/types/edge-types';
import { MissingFileError } from '@/stores/workflow-validation-store';
import { PreviewSlotCard } from '../custom-node/generic-node/preview-slot-card';
import { NodeItem } from './node-list';
import { useReactFlow } from '@xyflow/react';
import { SELECT_NODE_PLACEHOLDER_STRUCT } from '@/pages/workflow/component/custom-node/select-node/select-node';
import { toTitleCase } from '@/utils/string-case';

export const ErrorsTabContent: React.FC = () => {
  const {
    unsupportedNodes,
    missingFiles,
    fieldErrors,
    nodeListUnavailable,
    multipleWorkflows,
    cyclicGraph,
    removeMissingFile,
  } = useWorkflowValidationStore();
  const { openWorkflowPanel, setHighlightedField } = useWorkflowPanelStore();
  const {
    nodes,
    edges,
    setNodes,
    setEdges,
    handleParameterChange,
    getCompatibleNodes,
    getCompatibleSourceNodes,
    selectNode,
    deselectAllNodes,
  } = useNodeStore();
  const { currentWorkflow } = useWorkflowStore();
  const notification = useNotification();
  const { setCenter } = useReactFlow();

  const totalErrors =
    unsupportedNodes.length +
    missingFiles.length +
    fieldErrors.length +
    (nodeListUnavailable ? 1 : 0) +
    (multipleWorkflows !== null ? 1 : 0) +
    (cyclicGraph !== null ? 1 : 0);

  const handleUploadFile = useCallback(
    (errorId: string, nodeId: string, fieldId: string) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.style.display = 'none';

      input.onchange = async (event) => {
        const file = (event.target as HTMLInputElement).files?.[0];
        if (!file) {
          document.body.removeChild(input);
          return;
        }

        try {
          const workflowId = currentWorkflow?.id;
          if (!workflowId) {
            notification.error('Workflow ID not found');
            return;
          }

          const uploadResponse: FileUploadResponse = await uploadFile(
            file,
            workflowId,
            nodeId,
          );

          if (uploadResponse.success && uploadResponse.file) {
            handleParameterChange(nodeId, fieldId, uploadResponse);
            removeMissingFile(errorId);
            notification.success(`File "${file.name}" uploaded successfully`);
          } else {
            notification.error(
              `File upload failed: ${uploadResponse.message || 'Unknown error'}`,
            );
          }
        } catch (error) {
          console.error('File upload failed:', error);
          Sentry.captureException(error, {
            extra: {
              context: 'worfklow-canvas-uploading-missing-file-error',
              nodeId,
              fieldId,
            },
          });
          notification.error('File upload failed');
        } finally {
          document.body.removeChild(input);
        }
      };

      document.body.appendChild(input);
      input.click();
    },
    [currentWorkflow, handleParameterChange, removeMissingFile, notification],
  );

  const handleReplaceNode = useCallback(
    (errorId: string, nodeId: string) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return;

      const incoming = edges.filter((e) => e.target === nodeId);
      const outgoing = edges.filter((e) => e.source === nodeId);

      let selectDataType: 'source' | 'target';
      let compatibleNodes: NodeStruct[] = [];
      let acceptedTypes: HandleType[] = [];
      const extraData: Record<string, unknown> = {};

      let nextEdges: CustomEdge[];

      if (incoming.length > 0) {
        // Upstream → this node: SelectNode renders a target handle (left); data.type must be 'source'
        const upstream = nodes.find((n) => n.id === incoming[0].source);
        const struct = upstream?.data?.struct as NodeStruct | undefined;
        if (!struct) {
          notification.error('Could not read upstream node for replacement');
          return;
        }
        selectDataType = 'source';
        compatibleNodes = getCompatibleNodes(struct);
        const outPort = struct.output_ports.find(
          (p) => p.id === incoming[0].sourceHandle,
        );
        acceptedTypes =
          outPort?.port_type.accepted_types ||
          struct.output_ports[0]?.port_type.accepted_types ||
          [];

        nextEdges = edges
          .filter((e) => e.source !== nodeId)
          .map((e) =>
            e.target === nodeId
              ? ({
                  ...e,
                  targetHandle: 'select-node-target-handle',
                } as CustomEdge)
              : (e as CustomEdge),
          );
      } else if (outgoing.length > 0) {
        // This node → downstream: SelectNode renders a source handle (right); data.type must be 'target'
        const edge = outgoing[0] as CustomEdge;
        const downstream = nodes.find((n) => n.id === edge.target);
        const struct = downstream?.data?.struct as NodeStruct | undefined;
        if (!struct) {
          notification.error('Could not read downstream node for replacement');
          return;
        }
        selectDataType = 'target';
        compatibleNodes = getCompatibleSourceNodes(
          struct,
          edge.targetHandle || undefined,
        );
        const inputPort =
          struct.input_ports.find((p) => p.id === edge.targetHandle) ||
          struct.params.find(
            (p) => p.bindable_config?.input_port?.id === edge.targetHandle,
          )?.bindable_config?.input_port;
        acceptedTypes = inputPort?.port_type.accepted_types || [];

        extraData.targetNodeId = edge.target;
        extraData.targetHandleId = edge.targetHandle;

        nextEdges = edges
          .filter((e) => e.target !== nodeId)
          .map((e) =>
            e.source === nodeId
              ? ({
                  ...e,
                  sourceHandle: 'select-node-source-handle',
                } as CustomEdge)
              : (e as CustomEdge),
          );
      } else {
        selectDataType = 'source';
        nextEdges = edges as CustomEdge[];
      }

      const updatedNode = {
        ...node,
        type: NodeType.SelectNode as string,
        data: {
          ...node.data,
          struct: SELECT_NODE_PLACEHOLDER_STRUCT,
          compatibleNodes,
          acceptedTypes,
          type: selectDataType,
          ...extraData,
          pendingUnsupportedErrorId: errorId,
        },
      };

      const updatedNodes = nodes.map((n) =>
        n.id === nodeId ? updatedNode : n,
      );
      setNodes(updatedNodes);
      setEdges(nextEdges);

      setCenter(node.position.x + 100, node.position.y + 50, {
        zoom: 1,
        duration: 500,
      });

      notification.success('Node converted to Select Node for replacement');
    },
    [
      nodes,
      edges,
      setNodes,
      setEdges,
      getCompatibleNodes,
      getCompatibleSourceNodes,
      setCenter,
      notification,
    ],
  );

  const handleFocusNode = useCallback(
    (nodeId: string) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (node) {
        setCenter(node.position.x + 100, node.position.y + 50, {
          zoom: 1,
          duration: 500,
        });
      }
    },
    [nodes, setCenter],
  );

  const handleMissingFileParameterChange = useCallback(
    (error: MissingFileError, fieldId: string, value: unknown) => {
      handleParameterChange(error.nodeId, fieldId, value as InputValue);
      const upload = value as FileUploadResponse | null | undefined;
      if (
        upload &&
        typeof upload === 'object' &&
        upload.success &&
        upload.file
      ) {
        removeMissingFile(error.id);
        return;
      }
      if (value === null || value === '') {
        removeMissingFile(error.id);
      }
    },
    [handleParameterChange, removeMissingFile],
  );

  const handleViewNode = useCallback(
    (nodeId: string, fieldId: string | null = null) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (node) {
        setCenter(node.position.x + 100, node.position.y + 50, {
          zoom: 1,
          duration: 500,
        });
      }
      deselectAllNodes();
      selectNode(nodeId);
      openWorkflowPanel('configure', { nodeId });
      setHighlightedField(nodeId, fieldId);
    },
    [
      nodes,
      setCenter,
      deselectAllNodes,
      selectNode,
      openWorkflowPanel,
      setHighlightedField,
    ],
  );

  const groupedFieldErrors = useMemo(() => {
    const groups = new Map<
      string,
      { nodeName: string; errors: typeof fieldErrors }
    >();
    fieldErrors.forEach((error) => {
      if (!groups.has(error.nodeId)) {
        groups.set(error.nodeId, { nodeName: error.nodeName, errors: [] });
      }
      groups.get(error.nodeId)!.errors.push(error);
    });
    return Array.from(groups.entries()).map(
      ([nodeId, { nodeName, errors }]) => ({
        nodeId,
        nodeName,
        errors,
      }),
    );
  }, [fieldErrors]);

  if (totalErrors === 0) {
    return (
      <div className='flex flex-col items-center justify-center h-full p-8 text-center'>
        <AlertCircle className='h-12 w-12 text-muted-foreground mb-4' />
        <p className='text-sm text-muted-foreground'>
          No workflow issues detected
        </p>
      </div>
    );
  }

  return (
    <div className='flex-1 overflow-y-auto p-4 space-y-6'>
      <div className='flex items-center gap-2 mb-2'>
        <AlertTriangle className='size-5 text-destructive' />
        <span className='text-md font-medium'>
          Workflow Issues ({totalErrors})
        </span>
      </div>
      <p className='text-sm text-muted-foreground mb-4'>
        The following issues were detected in this workflow. Please resolve them
        to ensure proper workflow execution.
      </p>

      {missingFiles.length > 0 && (
        <div className='space-y-3'>
          <div className='flex items-center gap-2'>
            <FilesIcon className='size-5 text-destructive' />
            <span className='text-sm font-medium'>
              Missing Files ({missingFiles.length})
            </span>
          </div>
          <div className='space-y-2'>
            {missingFiles.map((error) => {
              const node = nodes.find((n) => n.id === error.nodeId);
              const struct = node?.data?.struct;
              const fileField =
                struct?.params?.find((p) => p.field_id === error.fieldId) ??
                struct?.params?.find(
                  (p) => p.display_modal === NodeInputType.FILE_UPLOAD,
                );

              return (
                <div
                  key={error.id}
                  className='rounded-xl p-3 bg-neutral-100 dark:bg-neutral-800/70'
                >
                  <div className='flex items-center justify-between gap-3'>
                    <button
                      type='button'
                      onClick={() => handleFocusNode(error.nodeId)}
                      className='text-left text-sm font-medium text-foreground min-w-0 truncate hover:underline'
                      title={error.nodeName}
                    >
                      {error.nodeName}
                    </button>
                    <Button
                      size='sm'
                      variant='ghost'
                      onClick={() =>
                        handleUploadFile(error.id, error.nodeId, error.fieldId)
                      }
                      className='shrink-0 gap-1.5 h-8 px-2 text-foreground'
                    >
                      <Redo2 className='size-5' />
                      Replace File
                    </Button>
                  </div>
                  {fileField ? (
                    <div className='mt-2'>
                      <PreviewSlotCard
                        nodeId={error.nodeId}
                        field={fileField}
                        workflowId={currentWorkflow?.id}
                        handleParameterChange={(fieldId, value) =>
                          handleMissingFileParameterChange(
                            error,
                            fieldId,
                            value,
                          )
                        }
                        mode='file'
                        nodeName={struct?.display_name}
                        nodeType={struct?.node_type}
                        isLocked={Boolean(node?.data?.isLocked)}
                        hideOutputHandle
                      />
                    </div>
                  ) : (
                    <p className='mt-2 text-sm text-muted-foreground truncate'>
                      {error.fileName}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {nodeListUnavailable && (
        <div className='space-y-3'>
          <div className='flex items-center gap-2'>
            <AlertCircleIcon className='size-5 text-destructive' />
            <span className='text-sm font-medium'>Unsupported Nodes</span>
          </div>
          <div
            key='node-list-unavailable'
            className='rounded-xl p-3 bg-neutral-100 dark:bg-neutral-800/70'
          >
            <p className='text-sm text-semibold'>
              {
                "We can't verify node compatibility because the node catalog isn't available. Refresh the page and try again. If the problem continues, contact support via feedback."
              }
            </p>
          </div>
        </div>
      )}

      {!nodeListUnavailable && unsupportedNodes.length > 0 && (
        <div className='space-y-3'>
          <div className='flex items-center gap-2'>
            <AppWindowIcon className='size-5 text-destructive' />
            <span className='text-sm font-medium'>
              Unsupported Nodes ({unsupportedNodes.length})
            </span>
          </div>
          <div className='space-y-2'>
            {unsupportedNodes.map((error) => {
              const node = nodes.find((n) => n.id === error.nodeId);
              const struct = node?.data?.struct;

              return (
                <div
                  key={error.id}
                  className='rounded-xl p-3 bg-neutral-100 dark:bg-neutral-800/70'
                >
                  <div className='flex items-center justify-between gap-3'>
                    <button
                      type='button'
                      onClick={() => handleFocusNode(error.nodeId)}
                      className='text-left text-sm font-medium text-foreground min-w-0 truncate hover:underline'
                      title={`${toTitleCase(error.nodeType)} (${error.nodeSubType})`}
                    >
                      {toTitleCase(error.nodeType)}
                    </button>
                    <div className='flex items-center gap-1 shrink-0'>
                      <Button
                        size='sm'
                        variant='ghost'
                        onClick={() =>
                          handleReplaceNode(error.id, error.nodeId)
                        }
                        className='gap-1.5 h-8 px-2 text-foreground'
                      >
                        <Redo2 className='size-5' />
                        Replace Node
                      </Button>
                    </div>
                  </div>
                  {struct ? (
                    <div className='mt-2'>
                      <NodeItem
                        node={struct}
                        showGripIcon={false}
                        isLocked={Boolean(node?.data?.isLocked)}
                        onClick={() => handleFocusNode(error.nodeId)}
                        elevated
                      />
                    </div>
                  ) : (
                    <p className='mt-2 text-sm text-muted-foreground truncate'>
                      {error.nodeName}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {multipleWorkflows !== null && (
        <div className='space-y-3'>
          <div className='flex items-center gap-2'>
            <NetworkIcon className='size-5 text-destructive' />
            <span className='text-sm font-medium'>
              Multiple Workflows ({multipleWorkflows.componentCount})
            </span>
          </div>

          <p className='text-sm font-medium text-muted-foreground mb-2'>
            Canvas contains {multipleWorkflows.componentCount} disconnected
            workflows. Connect all components or remove isolated nodes.
          </p>

          {multipleWorkflows.components.map((componentIds, index) => (
            <div
              key={index}
              className='rounded-xl p-3 bg-neutral-100 dark:bg-neutral-800/70'
            >
              <div className='flex items-center justify-between gap-3 text-sm font-medium text-foreground'>
                Workflow {index + 1}
              </div>
              <div className='mt-2 space-y-1'>
                {componentIds.map((nodeId) => {
                  const node = nodes.find((n) => n.id === nodeId);
                  const struct = node?.data?.struct;
                  return struct ? (
                    <NodeItem
                      key={nodeId}
                      node={struct}
                      showGripIcon={false}
                      isLocked={Boolean(node?.data?.isLocked)}
                      onClick={() => handleFocusNode(nodeId)}
                      elevated
                    />
                  ) : (
                    <p
                      key={nodeId}
                      className='text-sm text-muted-foreground truncate'
                    >
                      {nodeId}
                    </p>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {cyclicGraph !== null && (
        <div className='space-y-3'>
          <div className='flex items-center gap-2'>
            <GitBranchIcon className='size-5 text-destructive' />
            <span className='text-sm font-medium'>Cyclic Graph</span>
          </div>
          <p className='text-sm font-medium mb-2 text-muted-foreground'>
            Workflow contains a cycle. All connections must flow in one
            direction (no loops).
          </p>
          <div className='rounded-xl p-3 bg-neutral-100 dark:bg-neutral-800/70'>
            <div className='flex items-center justify-between gap-3 text-sm font-medium text-foreground'>
              Cycle Detected
            </div>
            <div className='space-y-1 mt-2'>
              {cyclicGraph.cycleNodeIds.map((nodeId) => {
                const node = nodes.find((n) => n.id === nodeId);
                const struct = node?.data?.struct;
                return struct ? (
                  <NodeItem
                    key={nodeId}
                    node={struct}
                    showGripIcon={false}
                    isLocked={Boolean(node?.data?.isLocked)}
                    onClick={() => handleFocusNode(nodeId)}
                    elevated
                  />
                ) : (
                  <p
                    key={nodeId}
                    className='text-sm text-muted-foreground truncate'
                  >
                    {nodeId}
                  </p>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {groupedFieldErrors.length > 0 && (
        <div className='space-y-3'>
          <div className='flex items-center gap-2'>
            <TextCursorInputIcon className='size-5 text-destructive' />
            <span className='text-sm font-medium'>
              Missing Required Fields ({fieldErrors.length})
            </span>
          </div>
          <div className='space-y-2'>
            {groupedFieldErrors.map(({ nodeId, nodeName, errors }) => (
              <div
                key={nodeId}
                className='rounded-xl p-3 bg-neutral-100 dark:bg-neutral-800/70'
              >
                <div className='flex items-center justify-between gap-3'>
                  <button
                    type='button'
                    onClick={() => handleFocusNode(nodeId)}
                    className='text-left text-sm font-medium text-foreground min-w-0 truncate hover:underline'
                    title={nodeName}
                  >
                    {nodeName}
                  </button>
                  <Button
                    size='sm'
                    variant='ghost'
                    onClick={() => handleViewNode(nodeId)}
                    className='shrink-0 gap-1.5 h-8 px-2 text-foreground'
                  >
                    <EyeIcon className='size-4' />
                    View Node
                  </Button>
                </div>
                <div className='mt-2 space-y-1'>
                  {errors.map((error) => (
                    <div
                      key={error.id}
                      onClick={() =>
                        handleViewNode(error.nodeId, error.fieldId)
                      }
                      className='flex w-full items-center gap-3 rounded-xl bg-white dark:bg-neutral-800 p-2 cursor-pointer hover:bg-neutral-200 dark:hover:bg-neutral-900 transition-colors'
                    >
                      <div className='flex items-center justify-center rounded-lg dark:bg-neutral-100 bg-slate-600 size-8 shrink-0'>
                        <TextCursorInputIcon className='size-5 text-white dark:text-black' />
                      </div>
                      <span className='flex-1 min-w-0 truncate text-sm font-medium text-foreground'>
                        {error.fieldName}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
